import { useEffect } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';

import { FilesUploadResponse } from '@services/types';

import { errorMessageUnhandledCase } from '../constants';
import {
    useUpdateAgent,
    useUpdateAvatarSupportedLanguages,
    useUpdateGeneralKnowledgeFiles,
    useUpdateGeneralPredefinedPrompts,
    useUpdatePredefinedPromptsByRole,
} from '../services/queries';
import {
    AgentConfig,
    AvatarSupportedLanguages,
    FilesDataToUpdate,
    GroupsWithPromptsRequest,
    RolesWithPromptsRequest,
} from '../services/types';
import { MenuOptionValue } from '../types';

type UseUpdateFormResult =
    | {
          type: MenuOptionValue.AgentConfiguration;
          mutate: UseMutateFunction<void, Error, AgentConfig>;
          isSending: boolean;
          error: Error | null;
          isError: boolean;
          isSuccess: boolean;
      }
    | {
          type: MenuOptionValue.RegionalKnowledgeFiles;
          mutate: UseMutateFunction<FilesUploadResponse, Error, FilesDataToUpdate>;
          isSending: boolean;
          error: Error | null;
          isError: boolean;
          isSuccess: boolean;
      }
    | {
          type: MenuOptionValue.AvatarConfiguration;
          mutate: UseMutateFunction<void, Error, AvatarSupportedLanguages>;
          isSending: boolean;
          error: Error | null;
          isError: boolean;
          isSuccess: boolean;
      }
    | {
          type: MenuOptionValue.GeneralPredefinedPrompts;
          mutate: UseMutateFunction<void, Error, GroupsWithPromptsRequest>;
          isSending: boolean;
          error: Error | null;
          isError: boolean;
          isSuccess: boolean;
      }
    | {
          type: MenuOptionValue.PredefinedPromptsByRole;
          mutate: UseMutateFunction<void, Error, RolesWithPromptsRequest>;
          isSending: boolean;
          error: Error | null;
          isError: boolean;
          isSuccess: boolean;
      };

export const useUpdateForm = (selectedMenuOption: MenuOptionValue): UseUpdateFormResult => {
    const {
        mutate: updateAgent,
        isPending: isUpdatedAgentPending,
        error: updateAgentError,
        isError: isUpdatedAgentError,
        isSuccess: isUpdatedAgentSuccess,
        reset: resetUpdateAgent,
    } = useUpdateAgent();

    const {
        mutate: updateGeneralKnowledgeFiles,
        isPending: isUpdatedGeneralKnowledgeFilesPending,
        error: updateGeneralKnowledgeFilesError,
        isError: isUpdatedGeneralKnowledgeFilesError,
        isSuccess: isUpdatedGeneralKnowledgeFilesSuccess,
        reset: resetUpdateGeneralKnowledgeFiles,
    } = useUpdateGeneralKnowledgeFiles();

    const {
        mutate: updateAvatarSupportedLanguages,
        isPending: isUpdatedAvatarSupportedLanguagesPending,
        error: updateAvatarSupportedLanguagesError,
        isError: isUpdatedAvatarSupportedLanguagesError,
        isSuccess: isUpdatedAvatarSupportedLanguagesSuccess,
        reset: resetUpdateAvatarSupportedLanguages,
    } = useUpdateAvatarSupportedLanguages();

    const {
        mutate: updateGeneralPredefinedPrompts,
        isPending: isUpdatedGeneralPredefinedPromptsPending,
        error: updateGeneralPredefinedPromptsError,
        isError: isUpdatedGeneralPredefinedPromptsError,
        isSuccess: isUpdatedGeneralPredefinedPromptsSuccess,
        reset: resetUpdateGeneralPredefinedPrompts,
    } = useUpdateGeneralPredefinedPrompts();

    const {
        mutate: updatePredefinedPromptsByRole,
        isPending: isUpdatedPredefinedPromptsByRolePending,
        error: updatePredefinedPromptsByRoleError,
        isError: isUpdatedPredefinedPromptsByRoleError,
        isSuccess: isUpdatedPredefinedPromptsByRoleSuccess,
        reset: resetUpdatePredefinedPromptsByRole,
    } = useUpdatePredefinedPromptsByRole();

    useEffect(() => {
        resetUpdateAgent();
        resetUpdateGeneralKnowledgeFiles();
        resetUpdateAvatarSupportedLanguages();
        resetUpdateGeneralPredefinedPrompts();
        resetUpdatePredefinedPromptsByRole();
    }, [
        selectedMenuOption,
        resetUpdateAgent,
        resetUpdateGeneralKnowledgeFiles,
        resetUpdateAvatarSupportedLanguages,
        resetUpdateGeneralPredefinedPrompts,
        resetUpdatePredefinedPromptsByRole,
    ]);

    switch (selectedMenuOption) {
        case MenuOptionValue.AgentConfiguration:
            return {
                type: MenuOptionValue.AgentConfiguration,
                mutate: updateAgent,
                isSending: isUpdatedAgentPending,
                error: updateAgentError,
                isError: isUpdatedAgentError,
                isSuccess: isUpdatedAgentSuccess,
            };
        case MenuOptionValue.RegionalKnowledgeFiles:
            return {
                type: MenuOptionValue.RegionalKnowledgeFiles,
                mutate: updateGeneralKnowledgeFiles,
                isSending: isUpdatedGeneralKnowledgeFilesPending,
                error: updateGeneralKnowledgeFilesError,
                isError: isUpdatedGeneralKnowledgeFilesError,
                isSuccess: isUpdatedGeneralKnowledgeFilesSuccess,
            };
        case MenuOptionValue.AvatarConfiguration:
            return {
                type: MenuOptionValue.AvatarConfiguration,
                mutate: updateAvatarSupportedLanguages,
                isSending: isUpdatedAvatarSupportedLanguagesPending,
                error: updateAvatarSupportedLanguagesError,
                isError: isUpdatedAvatarSupportedLanguagesError,
                isSuccess: isUpdatedAvatarSupportedLanguagesSuccess,
            };
        case MenuOptionValue.GeneralPredefinedPrompts:
            return {
                type: MenuOptionValue.GeneralPredefinedPrompts,
                mutate: updateGeneralPredefinedPrompts,
                isSending: isUpdatedGeneralPredefinedPromptsPending,
                error: updateGeneralPredefinedPromptsError,
                isError: isUpdatedGeneralPredefinedPromptsError,
                isSuccess: isUpdatedGeneralPredefinedPromptsSuccess,
            };
        case MenuOptionValue.PredefinedPromptsByRole:
            return {
                type: MenuOptionValue.PredefinedPromptsByRole,
                mutate: updatePredefinedPromptsByRole,
                isSending: isUpdatedPredefinedPromptsByRolePending,
                error: updatePredefinedPromptsByRoleError,
                isError: isUpdatedPredefinedPromptsByRoleError,
                isSuccess: isUpdatedPredefinedPromptsByRoleSuccess,
            };
        default: {
            const _exhaustiveCheck: never = selectedMenuOption;
            throw new Error(`${errorMessageUnhandledCase} ${_exhaustiveCheck}`);
        }
    }
};
